// Session store for the SPA.
//
// Refresh tokens live in an HttpOnly `eb_refresh` cookie set by the API at
// /v1/auth/login (and rotated on /v1/auth/refresh). They never touch JS.
// Access tokens are short-lived JWTs (15 min) held only in this module's
// in-memory `accessToken` variable — never persisted, so a single XSS sink
// can no longer leak the long-lived credential.
//
// User profile, the active organisation slug, and impersonation banner
// state remain in localStorage. They're display state, not credentials,
// and stripping them on tab close would force a re-bootstrap on every
// reload (the bootstrap hook silently re-mints an access token from the
// cookie).

const ORG_KEY = "exambase.org_slug";
const USER_KEY = "exambase.user";
const IMPERSONATION_KEY = "exambase.impersonating";

export interface ImpersonationState {
  target_organisation_id: string;
  target_slug: string;
  started_at: string;
  // Server fields available when state is hydrated from /auth/me.
  session_id?: string;
  reason?: string | null;
  reason_category?: string | null;
  view_as_role?:
    | "tenant_admin"
    | "moderator"
    | "teacher"
    | "registrar"
    | "student"
    | null;
  view_as_readonly?: boolean;
  expires_at?: string | null;
}

// Custom event name used to notify same-tab listeners (the banner, mostly)
// when the impersonation state in localStorage has changed. Native
// `storage` events only fire across tabs — so without this, a same-tab
// write via setImpersonation() would not propagate to the banner until
// the next reload. The banner subscribes to both.
export const IMPERSONATION_EVENT = "exambase:impersonation-changed";

export function setImpersonation(state: ImpersonationState | null): void {
  if (state) localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
  else localStorage.removeItem(IMPERSONATION_KEY);
  try {
    window.dispatchEvent(new Event(IMPERSONATION_EVENT));
  } catch {
    // Non-browser (SSR, test) — noop.
  }
}

export function getImpersonation(): ImpersonationState | null {
  const raw = localStorage.getItem(IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
}

export interface StoredUser {
  id: string;
  email: string;
  full_name: string;
  role?: string;
  is_examiner?: boolean;
  active_organisation_id?: string | null;
  // Founder-grace verify state. `needs_verification` drives the persistent
  // "verify your email" banner; `verification_grace_until` is the deadline a
  // founder-in-grace has before the account locks. Both come from
  // login / /auth/me (the server is authoritative).
  needs_verification?: boolean;
  verification_grace_until?: string | null;
}

// Module-level access token. Never written to storage; the bootstrap
// hook re-mints it from the HttpOnly refresh cookie on every page load.
let accessToken: string | null = null;
const accessTokenListeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const fn of accessTokenListeners) {
    try {
      fn(token);
    } catch {
      /* swallow — a misbehaving listener shouldn't break auth */
    }
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Subscribe to access-token changes. Returns an unsubscribe function.
 *  Used by useSessionExpiry to re-arm its expiry timer when the token
 *  rotates without a re-mount. */
export function onAccessTokenChange(fn: (token: string | null) => void): () => void {
  accessTokenListeners.add(fn);
  return () => {
    accessTokenListeners.delete(fn);
  };
}

export function clearSession(): void {
  setAccessToken(null);
  localStorage.removeItem(ORG_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(IMPERSONATION_KEY);
}

export function setActiveOrgSlug(slug: string | null): void {
  if (slug) localStorage.setItem(ORG_KEY, slug);
  else localStorage.removeItem(ORG_KEY);
}
export function getActiveOrgSlug(): string | null {
  return localStorage.getItem(ORG_KEY);
}

export function setUser(u: StoredUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(u));
}
export function getUser(): StoredUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

/** True when the user is signed in *and* we currently hold a fresh
 *  access token. After bootstrap this matches the previous behaviour;
 *  during the brief bootstrap window it returns false even if a stored
 *  user exists, so callers don't fire authed requests against a missing
 *  token. */
export function isLoggedIn(): boolean {
  return accessToken != null && getUser() != null;
}

/** Cheaper "do we even have a stored profile?" check used by the
 *  bootstrap path before it tries to refresh. */
export function hasStoredUser(): boolean {
  return getUser() != null;
}
